import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppContainer from "../../common/app_container";
import Alert, { AlertProps } from "../../common/alert";
import { TalentApplication, TalentApplicationStatusUpdate } from "@src/model/talent_application";
import { useTalentApplicationAPI } from "@src/api/talent_application_api";
import { useInvitationCodeAPI } from "@src/api/invitation_code_api";
import { CheckIcon, EnvelopeIcon, ListBulletIcon, StopIcon, TrashIcon, UserIcon, ChartBarIcon } from "@heroicons/react/20/solid";
import ImageContainer from "./image_container";
import { UploadedFile } from "../../api/file_api";
import Lightbox from "../../common/lightbox";
import BackButton from "@src/common/components/back-button";
import { TrainingAnalysisResponse } from "../../api/training_analysis_api";

const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
    } catch (e) {
        return "Invalid Date";
    }
};

const TalentApplicationReview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const {
        getApplication,
        updateApplicationStatus,
        deleteApplication,
        removeTrainingFile,
        resendWelcomeEmail,
        uploadFile,
        getAnalysisStatus
    } = useTalentApplicationAPI();
    const { getCode } = useInvitationCodeAPI();
    const navigate = useNavigate();
    const [application, setApplication] = useState<TalentApplication | null>(null);
    const [analysisData, setAnalysisData] = useState<TrainingAnalysisResponse | null>(null);
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState<string | boolean>(false);
    const [lightboxImage, setLightboxImage] = useState<UploadedFile | null>(null);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            setStatus(null);
            getApplication(id)
                .then(setApplication)
                .catch((err: any) => setStatus({ type: "error", message: `Error loading application: ${err.message}` }))
                .finally(() => setIsLoading(false));
        } else {
            setStatus({ type: "error", message: "Application ID is missing." });
            setIsLoading(false);
        }
    }, [id]);

    // New effect to fetch the invitation code if needed
    useEffect(() => {
        if (application && application.invitation_code_id && !application.invitation_code) {
            getCode(application.invitation_code_id)
                .then((codeData) => {
                    setApplication(prev => prev ? { ...prev, invitationCode: codeData } : prev);
                })
                .catch(err => console.error("Failed to fetch invitation code:", err));
        }
    }, [application, getCode]);

    // Effect to fetch analysis data when application is loaded
    useEffect(() => {
        if (application && id) {
            getAnalysisStatus(id)
                .then(setAnalysisData)
                .catch(err => console.error("Failed to fetch analysis data:", err));
        }
    }, [application, id]);

    const triggerApprovedActions = async (applicationId: string) => {
        console.log(`Triggering approved actions for application ${applicationId}`);
        setStatus({ type: "success", message: "Application approved and additional actions triggered." });
    };

    const handleStatusUpdate = async (newStatus: string) => {
        if (!application) return;
        setIsActionLoading(newStatus);
        setStatus(null);
        try {
            const updateData: TalentApplicationStatusUpdate = { status: newStatus };
            const updatedApp = await updateApplicationStatus(application.id, updateData);
            setApplication(updatedApp);
            setStatus({ type: "success", message: `Application status updated to ${newStatus}.` });
            if (newStatus.toUpperCase() === "APPROVED") {
                await triggerApprovedActions(updatedApp.id);
            }
        } catch (err: any) {
            setStatus({ type: "error", message: `Error updating status: ${err.message}` });
        } finally {
            setIsActionLoading(false);
        }
    };

    const handledWelcomeEmail = async () => {
        if (!application) return;
        setIsActionLoading("email");
        setStatus(null);
        try {
            await resendWelcomeEmail(application.id);
            setStatus({ type: "success", message: "Welcome email sent successfully." });
        } catch (err: any) {
            setStatus({ type: "error", message: `Error sending welcome email: ${err.message}` });
        } finally {
            setIsActionLoading(false);
        }
    }

    const handleDelete = async () => {
        if (!application || !window.confirm("Are you sure you want to permanently delete this application?")) return;
        setIsActionLoading("delete");
        setStatus(null);
        try {
            await deleteApplication(application.id);
            setStatus({ type: "success", message: "Application deleted successfully." });
            navigate("/talent-applications");
        } catch (err: any) {
            setStatus({ type: "error", message: `Error deleting application: ${err.message}` });
            setIsActionLoading(false);
        }
    };

    const handleSendFeedback = async () => {
        if (!application) return;
        const feedback = window.prompt("Enter feedback to send to the applicant:");
        if (!feedback) return;
        console.log(`Sending feedback for application ${application.id}: ${feedback}`);
        updateApplicationStatus(id || "", { status: "CHANGES_REQUESTED", comment: feedback });
        setStatus({ type: "success", message: "Feedback sent successfully." });
    };
    const deleteImage = (file: UploadedFile) => {
        if (!id || !file || !file.id) return;
        if (window.confirm("Are you sure you want to delete this image?")) {
            removeTrainingFile(id, file.id)
                .then((updatedApp) => {
                    setApplication(updatedApp);
                    setStatus({ type: "success", message: "Image deleted successfully." });
                })
                .catch((err: any) => {
                    setStatus({ type: "error", message: `Error deleting image: ${err.message}` });
                })

        }

    }

    const handleCropImage = async (originalFile: UploadedFile, croppedFile: File) => {
        if (!id || !originalFile || !originalFile.id) return;

        try {
            setStatus(null);

            // Upload the cropped file
            const uploadResponse = await uploadFile({
                files: [croppedFile],
                is_public: true,
                object_id: id,
                object_type: "TALENT_APPLICATION",
                generate_thumbnail: true,
                usage: "training"
            }, application?.auth_code || "", () => { }); // Empty progress handler

            if (uploadResponse && uploadResponse.length > 0) {
                // Remove the original file and add the new cropped one
                const updatedApp = await removeTrainingFile(id, originalFile.id);
                setApplication(updatedApp);
                setStatus({ type: "success", message: "Image cropped and updated successfully." });
            }
        } catch (err: any) {
            setStatus({ type: "error", message: `Error cropping image: ${err.message}` });
        }
    };


    if (isLoading) {
        return <AppContainer><div className="p-6">Loading application details...</div></AppContainer>;
    }

    if (!application) {
        return (
            <AppContainer>
                <div className="p-6">
                    {status && <Alert {...status} onClose={() => setStatus(null)} />}
                    {!status && <p>Application not found or ID is invalid.</p>}
                    <BackButton variant="secondary" onClick={() => navigate("/talent-applications")} />
                </div>
            </AppContainer>
        );
    }


    const applicantFullName = [application.first_name, application.last_name].filter(Boolean).join(" ") || "N/A";

    return (
        <AppContainer>
            <BackButton variant="ghost" onClick={() => navigate("/talent-applications")} />
            <div className="p-6">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
                    <h1 className="text-2xl font-semibold">{applicantFullName}</h1>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => handleStatusUpdate("REJECTED")}
                            disabled={!!isActionLoading}
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <StopIcon className="w-5" /> Reject
                        </button>
                        <button
                            onClick={() => handleStatusUpdate("APPROVED")}
                            disabled={!!isActionLoading}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <CheckIcon className="w-5" /> Approve
                        </button>
                        <button
                            onClick={handleSendFeedback}
                            disabled={!!isActionLoading}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <ListBulletIcon className="w-5" /> Send Feedback
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={!!isActionLoading}
                            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <TrashIcon className="w-5" /> Delete
                        </button>
                        {application.status === "APPROVED" && <button
                            onClick={handledWelcomeEmail}
                            disabled={!!isActionLoading}
                            className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <EnvelopeIcon className="w-5" /> Resend Welcome Email
                        </button>
                        }
                        <a
                            href={application.admin_email_link}
                            target="_blank"
                            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                        >
                            <UserIcon className="w-5" /> Login As User
                        </a>
                    </div>
                </div>

                {status && <div className="mb-4"><Alert {...status} onClose={() => setStatus(null)} /></div>}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Personal Information</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Full Name:</strong> {applicantFullName}</p>
                            <p><strong>Email Address:</strong> {application.email}</p>
                            <p><strong>Phone Number:</strong> {application.phone_number || "N/A"}</p>
                            <p><strong>Gender:</strong> {application.gender || "N/A"}</p>
                            <p><strong>Pronouns:</strong> {application.pronouns || "N/A"}</p>
                            <p>
                                <strong>Status:</strong>
                                <span
                                    className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${application.status === "APPROVED"
                                        ? "bg-green-100 text-green-700"
                                        : application.status === "REJECTED"
                                            ? "bg-red-100 text-red-700"
                                            : "bg-blue-100 text-blue-700"
                                        }`}
                                >
                                    {application.status}
                                </span>
                            </p>
                            <p><img className="w-40 h-40 rounded-full object-cover" src={application.profile_image?.thumbnail} alt="profile image" /></p>
                            <p><strong>Submitted:</strong> {formatDate(application.created_at)}</p>
                            <p><strong>Last Updated:</strong> {formatDate(application.updated_at)}</p>
                        </div>
                    </div>

                    <div className="hidden md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Social & Links</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Instagram:</strong> {application.social_instagram || "N/A"}</p>
                            <p><strong>Facebook:</strong> {application.social_facebook || "N/A"}</p>
                            <p><strong>TikTok:</strong> {application.social_tiktok || "N/A"}</p>
                            <p><strong>YouTube:</strong> {application.social_youtube || "N/A"}</p>
                            <p><strong>Twitter:</strong> {application.social_twitter || "N/A"}</p>
                            <p><strong>Patreon:</strong> {application.social_patreon || "N/A"}</p>
                            <p><strong>Other:</strong> {application.social_other || "N/A"}</p>
                        </div>
                    </div>

                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Application Details</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Invitation Code:</strong> {application.invitation_code || "N/A"}</p>
                            <p><strong>Talent Types:</strong> {application.talent_types || "N/A"}</p>
                            <p><strong>Interests:</strong> {application.interests || "N/A"}</p>
                            <p><strong>Brand Representations:</strong> {application.brand_representations || "N/A"}</p>
                            <p><strong>Exclusivity Agreements:</strong> {application.category_exclusivity_agreements || "N/A"}</p>
                            <p><strong>Sensitive Content:</strong> {application.sensitive_content || "N/A"}</p>
                            
                            {/* Training Analysis Section */}
                            <div className="mt-4 pt-4 border-t">
                                <div className="flex items-center gap-2 mb-3">
                                    <ChartBarIcon className="w-5 h-5 text-blue-600" />
                                    <strong className="text-blue-600">Training Analysis</strong>
                                </div>
                                {analysisData ? (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <strong className="text-xs text-gray-600">Status</strong>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                analysisData.status === "COMPLETED" ? "bg-green-100 text-green-700" :
                                                analysisData.status === "IN_PROGRESS" ? "bg-yellow-100 text-yellow-700" :
                                                "bg-gray-100 text-gray-700"
                                            }`}>
                                                {analysisData.status}
                                            </span>
                                        </div>
                                        
                                        {analysisData.overall_quality_score !== undefined && (
                                            <div className="flex justify-between items-center">
                                                <strong className="text-xs text-gray-600">Overall Quality</strong>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    analysisData.overall_quality_score >= 0.8 ? "bg-green-100 text-green-700" :
                                                    analysisData.overall_quality_score >= 0.6 ? "bg-yellow-100 text-yellow-700" :
                                                    "bg-red-100 text-red-700"
                                                }`}>
                                                    {(analysisData.overall_quality_score * 10).toFixed(1)}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {analysisData.statistics && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between items-center">
                                                    <strong className="text-xs text-gray-600">Suitable Images</strong>
                                                    <span className="text-xs font-medium">
                                                        {analysisData.statistics.suitable_images}/{analysisData.statistics.total_images}
                                                    </span>
                                                </div>
                                                
                                            </div>
                                        )}
                                        
                                        
                                        {analysisData.recommendations && analysisData.recommendations.length > 0 && (
                                            <div>
                                                <strong className="text-xs text-gray-600 block mb-1">Recommendations</strong>
                                                <div className="space-y-1">
                                                    {analysisData.recommendations.map((rec, idx) => (
                                                        <div key={idx} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                                            {rec}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-500 italic">
                                        Analysis not available yet
                                    </div>
                                )}
                            </div>
                            
                            <div>
                                <strong>Images</strong> 
                                <div className="flex flex-col gap-4">
                                    {application.images?.map((it, idx) => {
                                        // Find analysis result for this specific image
                                        const imageAnalysis = analysisData?.results?.find(result => result.file_id === it.id);
                                        
                                        return (
                                            <div key={it.id} className="space-y-2">
                                                <ImageContainer 
                                                    id={it.id || idx.toString()} 
                                                    image={it}
                                                    imageAnalysis={imageAnalysis}
                                                    onRemove={() => {
                                                        deleteImage(it)
                                                    }}
                                                    onClick={() => {
                                                        setLightboxImage(it)
                                                    }}
                                                    onCrop={(croppedFile) => handleCropImage(it, croppedFile)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Verification Images</h2>
                        <div className="space-y-2 text-sm">
                            <div className="flex flex-col gap-4">
                                {application.verification_images && Object.entries(application.verification_images).map(([key, img]) => (
                                    <div key={img.permalink} className="space-y-2">
                                        <strong>{key.replace('_', ' ')}</strong>
                                        <div className="relative">
                                            <ImageContainer
                                                id={img.id || key}
                                                image={img}
                                                onRemove={() => {
                                                    deleteImage(img)
                                                }}
                                                onClick={() => {
                                                    setLightboxImage(img)
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {lightboxImage && <Lightbox imageUrl={lightboxImage.permalink || ""}
                onClose={() => setLightboxImage(null)}
                isOpen={true} />}
        </AppContainer>
    );
};

export default TalentApplicationReview;
